"use client";
import { useId, useEffect } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { useSelector } from "react-redux";
import dynamic from "next/dynamic";

import InfoSvg from "assets/svg/info_small.svg";
import PeopleSvg from "assets/svg/people.svg";
import MembershipFlagForPlatinum from "assets/svg/duration-of-membership-platinum.svg";

import { tooltipMeslekiYeterlilik } from "constants/mesleki-yeterlilik-belgesi";

import { stringToSlug } from "helpers/string-to-slug";
import { createImageUrl } from "helpers/create-image-url";
import { getToken } from "helpers/auth";
import { favoriteHelpers } from "@/src/helpers/favorite";

import { accountTypes } from "./widgets";

import Title from "components/listing-detail/title";
import SwiperWrapper from "components/swiper-wrapper";
import VerticalCard from "components/cards/vertical-card";
import { LazyImage } from "components/common/lazy-image/LazyImage";

import styles from "./styles.module.css";

const StickyFavoriteInfoBar = dynamic(() => import("components/common/sticky-favorite-info-bar"), { ssr: false });

const CompanyBadge = ({ data, similarData }) => {
  const keyId = useId();

  const getAllFavoritesByAccount = favoriteHelpers();

  const {
    account: { durationOfMembership, id, logo, membershipType, type, locationSummary },
    accountDocument,
    member: { name },
    memberDocument,
  } = data;

  const favoriteInfoBarButtonContent = useSelector((state) => state.favorite.favoriteInfoBarButtonContent);
  const favoriteInfoListingId = useSelector((state) => state.listingSearch.handleListingIdClickFavorite);
  const isMemberShipTypeSpecialPackage = membershipType === "platinum" || membershipType === "platinum plus";
  const specialPackageBadgeEquals = {
    platinum: "platinum_black",
    "platinum plus": "platinum_plus_black",
  };

  const accountSlug = data?.account?.name ? `${stringToSlug(data.account.name, "-")}-${id}` : id;
  const getAccountLogo =
    accountTypes[isMemberShipTypeSpecialPackage ? specialPackageBadgeEquals[membershipType] : membershipType];

  const handleInfoBarSuccessCallback = async () => {
    await getAllFavoritesByAccount();
  };

  const getMembershipYear = () =>
    isMemberShipTypeSpecialPackage ? (
      <div>
        <span className={styles.platinumYearWrap}>
          <span className={styles.platinumYearIconWrap}>
            <MembershipFlagForPlatinum />
          </span>
          <span className={styles.platinumYearRadial}>{durationOfMembership}.</span>
          <span className={styles.platinumYearFlag}>YIL</span>
        </span>
      </div>
    ) : (
      <span className={styles.yearBadge}>{`${durationOfMembership}. Yıl`}</span>
    );

  useEffect(() => {
    if (getToken()) getAllFavoritesByAccount();
  }, []);

  return (
    <div className={styles.wrapper} id="firma-kunyesi" data-scroll-selector>
      <Title>Firma Künyesi</Title>
      <div className={styles.inner}>
        <div className={styles.badgeWrap}>
          <div className={styles.info}>
            <div className={styles.logoWrap}>
              <a href={`/emlak-ofisleri-detay/${accountSlug}/`}>
                {logo !== null ? (
                  <LazyImage
                    src={createImageUrl(logo, {
                      width: 240,
                      height: 150,
                    })}
                    alt="firma_logo"
                  />
                ) : (
                  <PeopleSvg />
                )}
              </a>
            </div>
            <div className={styles.titlesWrap}>
              <h4 className={styles.userName}>
                <a href={`/emlak-ofisleri-detay/${accountSlug}/`}>{name}</a>
              </h4>
              <span className={styles.companyName}>{data?.account?.name}</span>
              {locationSummary ? <span className={styles.location}>{locationSummary}</span> : null}
            </div>
            {durationOfMembership > 0 && getMembershipYear()}
            {membershipType !== "standart" && <span className={styles.accountType}>{getAccountLogo}</span>}
          </div>
          <ul className={styles.textList}>
            {accountDocument?.no && (
              <li>
                Taşınmaz Ticari Yetki Belgesi <span className={styles.highlight}>{accountDocument?.no}</span>
              </li>
            )}
            {!accountDocument?.no && type !== "personal" && (
              <li>
                Taşınmaz Ticareti Yetki Belge Bilgileri Girilmemiştir
                {/* eslint-disable-next-line */}
                <span className={styles.infoWrap} tooltip={tooltipMeslekiYeterlilik} flow="down">
                  <InfoSvg />
                </span>
              </li>
            )}
            {memberDocument?.no && (
              <li>
                Mesleki Yeterlilik Belgesi <span className={styles.highlight}>{memberDocument.no}</span>
                {/* eslint-disable-next-line */}
                <span className={styles.infoWrap} tooltip={tooltipMeslekiYeterlilik} flow="down">
                  <InfoSvg />
                </span>
              </li>
            )}
          </ul>
        </div>
        {similarData?.length > 0 && (
          <div className={styles.listWrap}>
            <div className={styles.topWrap}>
              <h4 className={styles.listTitle}>Firmanın Diğer İlanları</h4>
              {similarData && similarData.length > 3 && (
                <a href={`/emlak-ofisleri-detay/${accountSlug}/`} className={styles.textLink}>
                  Diğer İlanlarını Gör
                </a>
              )}
            </div>
            <div className={styles.sliderWrap}>
              <SwiperWrapper>
                <Swiper navigation={true} loop={false} modules={[Navigation]} slidesPerView={3}>
                  <ul className={styles.cardList}>
                    {similarData.map((item, index) => (
                      <SwiperSlide key={`${keyId}-${index}`}>
                        <li>
                          <VerticalCard
                            media={item.coverPhoto}
                            title={item.title}
                            list={item.quickInfo}
                            locationSummary={item.locationSummary}
                            location={item.location}
                            price={item.price}
                            url={item.url}
                            id={item.id}
                          />
                        </li>
                      </SwiperSlide>
                    ))}
                  </ul>
                </Swiper>
              </SwiperWrapper>
            </div>
          </div>
        )}
      </div>
      <StickyFavoriteInfoBar
        listingId={favoriteInfoListingId}
        successCallback={handleInfoBarSuccessCallback}
        backButtonCondition={favoriteInfoBarButtonContent === "Geri Al"}
      />
    </div>
  );
};

CompanyBadge.propTypes = {
  data: PropTypes.object,
  similarData: PropTypes.array,
};

export default CompanyBadge;
